import {
	ColorPicker,
	ColorPickerReadOnly,
	FormItem,
	InputWithMoreInfo,
	Select,
	SelectItem,
	SelectReadOnly,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	RefObject,
	useEffect,
	useRef,
	useState
} from "react";
import {
	isCustomSaveAppColor} from "client_legacy/ndaw/appColor";
import { AppColorKind, SaveAppColorModel } from "client/Api/Apps/common";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { AppColorDarkThemeInfoDialog } from "./AppColorDarkThemeInfoDialog";

export type AppColorSelectFormItemProps = {
	attachable: IValueInputAttachable<SaveAppColorModel>;
	isReadOnly?: boolean;
};

export const AppColorSelectFormItem = (props: AppColorSelectFormItemProps): ReactElement => {
	const {
		attachable,
		isReadOnly = false
	} = props;

	const {
		value
	} = attachable;

	const [shouldShowColorInfo, setShouldShowColorInfo] = useState<boolean>(false);

	const lastSelectedCustomColorRef: RefObject<string | undefined> = useRef<string>(
		value.kind === AppColorKind.Custom
			? value.value
			: "#FF0000"
	);

	const kindChangeHandler = (newValue: AppColorKind):void => {
		let newColor: SaveAppColorModel;
		switch(newValue) {
			case AppColorKind.Custom:
				newColor = { kind: AppColorKind.Custom, value: lastSelectedCustomColorRef.current };
				break;
			case AppColorKind.Header:
			case AppColorKind.Primary:
			case AppColorKind.Inherit:
				newColor = { kind: newValue };
				break;
			default:
				never(newValue);
		}

		attachable.reducer.setValue(newColor);
		attachable.validation.triggerInputFinished();
	};

	useEffect(
		() => {
			if (value.kind === AppColorKind.Custom) {
				lastSelectedCustomColorRef.current = value.value;
			}
		},
		[value]
	);

	return (
		<>
			<FormItem
				label={localize("_Color")}
			>
				{isReadOnly
					? (
						<SelectReadOnly
							value={value.kind}
							options={colorKindOptions}
							trackingId={attachable.for("kind").reducer.path}
						/>
					)
					: (
						<>
							<InputWithMoreInfo
								onMoreInfo={() => setShouldShowColorInfo(true)}
							>
								<Select
									value={value.kind}
									onChange={kindChangeHandler}
									options={colorKindOptions}
									trackingId={attachable.for("kind").reducer.path}
									menuTitle={localize("_Color")}
								/>
								<ValidationError {...attachable.for("kind").attachToValidationError()} />
							</InputWithMoreInfo>
							{shouldShowColorInfo && (
								<AppColorDarkThemeInfoDialog
									onClose={() => setShouldShowColorInfo(false)}
								/>
							)}
						</>
					)
				}
			</FormItem>

			{attachable.whenType(
				isCustomSaveAppColor,
				customColorHandler => (
					<FormItem
						label={localize("_SelectCustomColor")}
					>
						{isReadOnly
							? (
								<ColorPickerReadOnly
									{...customColorHandler.for("value")
										.attachToColorPicker()
									}
								/>
							)
							: (
								<>
									<ColorPicker
										{...customColorHandler.for("value")
											.attachToColorPicker()
										}
										menuTitle={localize("_SelectCustomColor")}
									/>
									<ValidationError
										{...customColorHandler.for("value")
											.attachToValidationError()
										}
									/>
								</>
							)
						}
					</FormItem>
				))}
		</>
	);
};

AppColorSelectFormItem.displayName = "AppColorSelectFormItem";

const colorKindOptions: readonly SelectItem<AppColorKind>[] = [
	{ text: localize("_Default"), value: AppColorKind.Inherit },
	{ text: localize("_HeaderColor"), value: AppColorKind.Header },
	{ text: localize("_PrimaryColor"), value: AppColorKind.Primary },
	{ text: localize("_Custom"), value: AppColorKind.Custom },
];
